import React, { useEffect, useRef, useState } from 'react';
import { CSSProperties } from 'styled-components';
import Portal from './Portal';

const getPageOffset = () => {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 };
  }

  return {
    x:
      window.pageXOffset !== undefined
        ? window.pageXOffset
        : (
            document.documentElement ||
            document.body.parentNode ||
            document.body
          ).scrollLeft,
    y:
      window.pageYOffset !== undefined
        ? window.pageYOffset
        : (
            document.documentElement ||
            document.body.parentNode ||
            document.body
          ).scrollTop
  };
};

const EVENT_OPTIONS = {
  passive: true
};

const DEFAULT_OFFSET = { top: 0, left: 0, right: 0 };

interface IRelativePortalProps {
  width: number;
  fullWidth: boolean;
  component: any;
  active: boolean;
  zIndex: number;
  additionalOffset: {
    top: number;
    left: number;
    right: number;
    bottom: number;
  };
  portalId: string;
}

const RelativePortal = ({
  component: ProvidedComponent,
  children,
  width = 200,
  fullWidth = false,
  portalId,
  // active is used to hide the dropdowns off screen.
  // There is a valid use case where we'd want this SSR'd to the dom but hidden via css.
  active = true,
  zIndex = 1000,
  additionalOffset
}): React.FC<IRelativePortalProps> => {
  const [styles, setStyles] = useState<CSSProperties>({});
  const positionToElementRef = useRef<any>();

  const [animationFrameQueued, setAnimationFrameQueued] =
    useState<number | undefined>();

  const calculateOffset = () => {
    if (positionToElementRef?.current == null) return null;
    let offset = DEFAULT_OFFSET;

    const documentWidth = document.documentElement.clientWidth;

    const positionToElementRect =
      positionToElementRef.current.getBoundingClientRect();

    const pageOffset = getPageOffset();
    const topOffset =
      pageOffset.y + positionToElementRect.top + (additionalOffset.top ?? 0);
    let leftOffset =
      pageOffset.x + positionToElementRect.left + (additionalOffset.left ?? 0);
    let rightOffset =
      documentWidth -
      positionToElementRect.right -
      pageOffset.x +
      (additionalOffset.top ?? 0);

    if (typeof rightOffset === 'number') {
      if (rightOffset < 0) {
        rightOffset = 0;
      }
    } else if (width + leftOffset > pageOffset.x + documentWidth) {
      leftOffset = pageOffset.x + documentWidth - width;
    }

    if (
      topOffset !== offset.top ||
      leftOffset !== offset.left ||
      rightOffset !== offset.right
    ) {
      offset = {
        top: topOffset,
        left: leftOffset,
        right: rightOffset
      };
    }

    let newStyles: CSSProperties = {
      position: 'absolute',
      right: 'auto',
      left: 'auto',
      top: offset.top,
      width,
      zIndex
    };

    if (!active) {
      newStyles.right = '200%';
    } else {
      const adjustment = fullWidth
        ? { right: 0, left: 0 }
        : typeof additionalOffset?.right === 'number'
        ? { right: offset.right }
        : { left: offset.left };

      newStyles = { ...newStyles, ...adjustment };
    }

    if (
      typeof newStyles.width === 'number' &&
      typeof newStyles.left === 'number' &&
      // @ts-ignore
      newStyles.width + newStyles.left > documentWidth
    ) {
      newStyles.left = 'auto';
      newStyles.right = 0;
    }

    setStyles(newStyles);
    setAnimationFrameQueued(undefined);
  };

  const handleChange = () => {
    if (animationFrameQueued) return;
    setAnimationFrameQueued(requestAnimationFrame(calculateOffset));
  };

  // the use-case where this is hidden via css offscreen creates an issue
  // with the location of the element. Adding a recalc on show/hide to manage this issue
  useEffect(() => {
    handleChange();
  }, [active]);

  useEffect(() => {
    // listen for document clicks
    window.addEventListener('resize', handleChange, EVENT_OPTIONS);
    window.addEventListener('scroll', handleChange, EVENT_OPTIONS);

    calculateOffset();

    return () => {
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('scroll', handleChange);

      // https://betterprogramming.pub/5-common-javascript-memory-mistakes-c8553972e4c2
      window.cancelAnimationFrame(animationFrameQueued as number);
    };
  }, []);

  // @ts-ignore
  return (
    <ProvidedComponent ref={positionToElementRef}>
      <Portal portalId={portalId}>
        <div style={styles || DEFAULT_OFFSET}>{children}</div>
      </Portal>
    </ProvidedComponent>
  );
};

export default RelativePortal;
